import { LangStore } from "../store/LangStore";
import { tokenManager } from "./tokenManager";
import { JsonParseError } from "../common/utils";
import { errorProcessors, ErrorProcessorContext } from "./errorProcessors";
import { isStructuredErrorMeta } from "./errorTypes";

export type ApiError = {
  title: string;
  message: string;
  code: string;
  fieldName?: string;
  entityName?: string;
  serviceName?: string;
  // Structured error details for rich rendering
  detailType?: string;
  items?: Array<{ id?: string; label: string }>;
};

interface Error {
  name: string;
  message: string;
  stack?: string;
}

const prismaErrorKey = "PrismaError";
const dbErrorKey = "DbError";
const apiErrorKey = "ApiError";
const validationErrorKey = "ValidationError";
const unauthorizedErrorKey = "UnauthorizedError";

/**
 * ¿Este error es "la sesión venció"?
 *
 * Existe porque los stores llaman a showErrorDialog de dos formas —unos pasan
 * el Error, otros el `code` ya resuelto— y el chequeo original comparaba
 * contra el string. Los que pasaban el Error nunca matcheaban: en vez del
 * diálogo de sesión vencida mostraban un error genérico, o reventaban. Eran
 * 440 de 706 llamadas.
 *
 * Preguntar acá y no en cada store hace que la respuesta no dependa de con qué
 * lo llamen, ni de que quien agregue la próxima llamada se acuerde de la forma
 * correcta.
 *
 * Contempla las tres: el código suelto, un Error crudo, y un ApiError ya
 * resuelto. El caso del Error necesita pasar por getApiError porque la sesión
 * vencida también llega envuelta en el JSON de GraphQL como UNAUTHENTICATED,
 * donde `message` no dice nada.
 */
export const isSessionExpired = (
  originalError: any,
  langStore: LangStore
): boolean => {
  if (originalError === "SESSION_EXPIRED") return true;
  if (originalError instanceof Error) {
    return getApiError(originalError, langStore).code === "SESSION_EXPIRED";
  }
  return originalError?.code === "SESSION_EXPIRED";
};

export const getApiError = (error: Error, langStore: LangStore): ApiError => {
  if (error instanceof JsonParseError) {
    return {
      title: langStore.jsonValidationError || "JSON inválido",
      message:
        langStore.jsonFieldError(error.fieldName) ||
        `El campo "${error.fieldName}" contiene JSON inválido: ${error.originalError.message}`,
      code: "JSON_PARSE_ERROR",
      fieldName: error.fieldName,
    };
  }

  if (error.message === "SESSION_EXPIRED") {
    return {
      title: langStore.sessionExpiredTitle,
      message: langStore.sessionExpiredMessage,
      code: "SESSION_EXPIRED",
    };
  }

  try {
    const response: any = JSON.parse(
      error.message.slice(error.message.indexOf("{"))
    );
    const apiError = response.response.errors[0].extensions;

    if (apiError.code) {
      if (apiError.code === "UNAUTHENTICATED") {
        tokenManager.clearTokens();
        return {
          title: langStore.sessionExpiredTitle,
          message: langStore.sessionExpiredMessage,
          code: "SESSION_EXPIRED",
        };
      }

      // El server ya arma el mensaje que nombra la entidad y el campo, en el idioma
      // del usuario ("Ya existe un <entidad> con ese <campo>."). Un server anterior
      // manda solo el código como mensaje: entonces se arma acá, como antes.
      if (apiError.code === "P2002") {
        const serverMessage = response.response.errors[0]?.message;
        if (serverMessage && serverMessage !== apiError.code) {
          return {
            title: langStore.apiServerError(apiError.code),
            message: serverMessage,
            code: apiError.code,
            fieldName: apiError.meta?.target?.[0],
          };
        }
      }

      if (apiError.code === "P2002" && apiError.meta?.target) {
        const fields = apiError.meta.target.filter(
          (f: string) => !f.startsWith("tenant_")
        );

        if (fields.length > 0) {
          const modelName = apiError.meta.modelName;
          let fieldLabels: string[];

          if (modelName) {
            fieldLabels = fields.map((fieldName: string) =>
              langStore.getFieldLabel(modelName, fieldName)
            );
          } else {
            fieldLabels = fields;
          }

          return {
            title: langStore.apiServerError(apiError.code),
            message: langStore.uniqueConstraintMessage(fieldLabels),
            code: apiError.code,
            fieldName: fields[0],
          };
        }
      }

      if (
        apiError.code.startsWith("VALIDATION_") ||
        apiError.code === "OPTIMISTIC_LOCK_FAILED" ||
        apiError.code === "CONTRACT_VALIDATION_FAILED"
      ) {
        const entityName = apiError.meta?.entityName;
        // Preferir el mensaje real del server (lo escribió la validación de negocio,
        // p.ej. "La obra debe incluir la editorial «X»…"). El reconstruido genérico
        // (validationErrorMessage → "campo: no es válido") solo se usa para el caso
        // estructurado (meta.validationErrors[] con labels/constraints i18n) o si el
        // server no mandó mensaje. Así toda validación custom muestra su causa real.
        const serverMessage = response.response.errors[0]?.message;
        const validationMessage = apiError.meta?.validationErrors?.length
          ? langStore.validationErrorMessage(apiError.meta, entityName)
          : serverMessage ||
            langStore.validationErrorMessage(apiError.meta, entityName);
        return {
          title:
            langStore.apiServerError(apiError.code) ||
            {
              es: "Error de validación",
              en: "Validation error",
              pt: "Erro de validação",
            }[langStore.language],
          message: validationMessage,
          code: apiError.code,
          fieldName: apiError.meta?.field,
          entityName: entityName,
        };
      }

      if (
        apiError.code.endsWith("_NOT_FOUND") ||
        apiError.code === "NOT_FOUND"
      ) {
        return {
          title:
            langStore.apiServerError(apiError.code) ||
            {
              es: "No encontrado",
              en: "Not found",
              pt: "Não encontrado",
            }[langStore.language],
          message: langStore.notFoundErrorMessage(apiError.meta),
          code: apiError.code,
        };
      }

      if (
        apiError.code === "SYSTEM_UNKNOWN_ERROR" ||
        apiError.code.startsWith("UNKNOWN_")
      ) {
        return {
          title: langStore.apiServerError(apiError.code),
          message: langStore.unknownErrorMessage(apiError.meta),
          code: apiError.code,
        };
      }

      // Check for structured error with details
      if (
        apiError.meta &&
        (isStructuredErrorMeta(apiError.meta) || apiError.meta.validationErrors)
      ) {
        const context: ErrorProcessorContext = {
          langStore,
          language: langStore.language as "es" | "en" | "pt",
        };

        const processedMessage = errorProcessors.process(
          apiError.code,
          apiError.meta,
          context
        );

        return {
          title: langStore.apiServerError(apiError.code),
          message: processedMessage || langStore.apiServerError(apiError.code),
          code: apiError.code,
          detailType: apiError.meta.detailType,
          items: apiError.meta.items,
        };
      }

      // Código sin traducción registrada: preferir el mensaje REAL del server
      // antes que el genérico "No se pudo completar la operación". Cubre las
      // ValidationError con meta.code custom (p.ej. INSUFFICIENT_DISTINCT_PARTIES),
      // que pierden el prefijo VALIDATION_ y esquivan la rama de arriba — el
      // usuario veía el genérico con la causa real descartada.
      const fallbackServerMessage = response.response.errors[0]?.message;
      return {
        title: langStore.apiServerError(apiError.code),
        message:
          !langStore.hasApiServerError(apiError.code) && fallbackServerMessage
            ? fallbackServerMessage
            : langStore.apiServerError(apiError.code),
        code: apiError.code,
      };
    }

    if (apiError.code === prismaErrorKey) {
      return {
        title: "Error en la base de datos",
        message: apiError.message,
        code: apiError.code,
      };
    } else if (apiError.code === dbErrorKey) {
      return {
        title: "Datos incorrectos",
        message: apiError.message,
        code: apiError.code,
      };
    } else if (apiError.code === apiErrorKey) {
      return {
        title: "Error de la API",
        message: apiError.message,
        code: apiError.code,
      };
    } else if (apiError.code === validationErrorKey) {
      const { code, entityName, fieldName } = apiError;
      return {
        title: "Error de validación",
        message: `${fieldName} en ${entityName}`,
        fieldName,
        entityName,
        code,
      };
    } else if (apiError.code === unauthorizedErrorKey) {
      const { code, entityName, fieldName, serviceName } = apiError;
      return {
        title: langStore.authorizationError,
        message: langStore.doNotHavePermission,
        code: code,
        entityName,
        fieldName,
        serviceName,
      };
    }
    return {
      title: langStore.serverError,
      message: error.message,
      code: "",
    };
  } catch {
    return {
      title: langStore.serverError,
      message: error.message,
      code: "",
    };
  }
};
